import React, { Fragment, ReactNode, useRef } from 'react';
import {
    Cell,
    ColumnDef,
    ColumnSort,
    ExpandedState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Header,
    Row,
    RowSelectionState,
    Table,
    useReactTable,
    VisibilityState
} from '@tanstack/react-table';
import i18n from '../../common/i18n';
import clsx from 'clsx';
import { Pagination as MuiPagination } from '@material-ui/lab';

import './react-table.less';
import './react-table-lib.css';

export function ReactTable<T>({
    data,
    columns,
    pagination,
    sorting,
    rowSelection,
    columnVisibility,
    expanding,
    idKey,
    onTrProps,
    onThProps,
    onTdProps,
    onTableProps,
    style,
    className
}: {
    data: T[];
    columns: ColumnDef<T>[];
    pagination?: { pageSize: number; /** pagination will hide if not needed*/ hiding?: boolean; pageIndex?: number };
    sorting?: ColumnSort;
    rowSelection?: RowSelectionOpts;
    columnVisibility?: VisibilityState;
    expanding?: {
        renderSubComponent: ({ row }: { row: Row<T> }) => ReactNode;
        expandedState?: ExpandedState;
        className?: string;
    };
    idKey?: keyof T;
    onTrProps?: ({ row, table }: { row: Row<T>; table: Table<T> }) => any;
    onTdProps?: ({ cell, table }: { cell: Cell<T, unknown>; table: Table<T> }) => any;
    onThProps?: ({ header, table }: { header: Header<T, unknown>; table: Table<T> }) => any;
    onTableProps?: ({ table }: { table: Table<T> }) => any;
    style?: { striped?: boolean; border?: boolean; orangeHeader?: boolean; circledNumbersPaging?: boolean };
    className?: string;
}) {
    const table = useReactTable({
        data,
        columns,
        initialState: {
            pagination: {
                pageSize: pagination ? pagination.pageSize : data.length,
                pageIndex: pagination?.pageIndex || 0
            },
            sorting: sorting ? [sorting] : null
        },
        state: Object.assign(
            {
                rowSelection: rowSelection?.rowSelectionState,
                columnVisibility
            },
            expanding?.expandedState ? { expanded: expanding?.expandedState } : {}
        ),
        enableRowSelection: !!rowSelection,
        onRowSelectionChange: rowSelection?.setRowSelectionState,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowCanExpand: () => !!expanding?.renderSubComponent,
        getExpandedRowModel: expanding ? getExpandedRowModel() : null,
        getPaginationRowModel: pagination ? getPaginationRowModel() : null,
        getRowId: idKey ? row => row[idKey].toString() : null
    });
    const tableProps = onTableProps?.({ table });
    const displayPaging = pagination && (!pagination.hiding || pagination.pageSize < data.length);
    return (
        <div
            {...tableProps}
            className={clsx(
                'ReactTable',
                style?.border ? 'rt-container-brd' : 'rt-container',
                style?.striped && 'rt-container-striped',
                style?.orangeHeader && 'rt-th-orange',
                className,
                tableProps?.className
            )}
        >
            <table className="rt-table" role="grid">
                <thead className="rt-thead -header">
                    {table.getHeaderGroups().map(group => (
                        <tr key={group.id} className="rt-tr" data-atid={'table-header'}>
                            {group.headers.map(header => {
                                const props = onThProps?.({ header, table });
                                return (
                                    <th
                                        key={header.id}
                                        {...props}
                                        className={clsx('rt-th', props?.className, {
                                            '-cursor-pointer': sorting && header.column.getCanSort(),
                                            '-sort-desc': header.column.getIsSorted() === 'desc',
                                            '-sort-asc': header.column.getIsSorted() === 'asc'
                                        })}
                                        onClick={sorting ? header.column.getToggleSortingHandler() : () => null}
                                        colSpan={header.colSpan}
                                        style={styleFrom(header.column.columnDef)}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody className="rt-tbody">
                    {table.getRowModel().rows.map((row, i) => {
                        const props = onTrProps?.({ row, table });
                        return (
                            <Fragment key={row.id}>
                                <tr
                                    {...props}
                                    className={clsx('rt-tr', (i + 1) % 2 === 0 ? '-even' : '-odd', props?.className)}
                                    data-atid={'table-line'}
                                >
                                    {row.getVisibleCells().map(cell => {
                                        const props = onTdProps?.({ cell, table });
                                        return (
                                            <td
                                                key={cell.id}
                                                {...props}
                                                className={clsx('rt-td', props?.className)}
                                                style={styleFrom(cell.column.columnDef)}
                                            >
                                                {/*{cell.renderValue()}*/}
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {row.getIsExpanded() && expanding?.renderSubComponent && (
                                    <tr className={clsx('rt-tr', props?.className, expanding?.className)}>
                                        {/* 2nd row is a custom 1 cell row */}
                                        <td className="rt-td" colSpan={row.getVisibleCells().length}>
                                            {expanding.renderSubComponent({ row })}
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
            {displayPaging &&
                (style?.circledNumbersPaging ? <PaginationNg table={table} /> : <Pagination table={table} />)}
        </div>
    );
}

function styleFrom<T>(columnDef: ColumnDef<T>) {
    return {
        minWidth: columnDef.minSize,
        maxWidth: columnDef.maxSize < 10000 ? columnDef.maxSize : null,
        flex: columnDef.size
    };
}

function Pagination<T>({ table }: { table: Table<T> }) {
    const inputRef = useRef<HTMLInputElement>();
    return (
        <div className="-pagination">
            <div className="-previous">
                <button
                    type="button"
                    disabled={!table.getCanPreviousPage()}
                    className="-btn"
                    onClick={() => {
                        inputRef.current.value = table.getState().pagination.pageIndex.toString();
                        table.previousPage();
                    }}
                >
                    {i18n('nui.table.previous')}
                </button>
            </div>
            <div className="-center">
                <span className="-pageInfo">
                    {i18n('nui.table.page.title') + ' '}
                    <div className="-pageJump">
                        <input
                            aria-label="jump to page"
                            type="number"
                            defaultValue="1"
                            onBlur={e => table.setPageIndex(e.target.value ? Number(e.target.value) - 1 : 0)}
                            onChange={e => {
                                e.target.value = Math.min(Number(e.target.value), table.getPageCount()).toString();
                                e.target.value = Math.max(Number(e.target.value), 1).toString();
                            }}
                            ref={inputRef}
                        />
                    </div>
                    {' ' + i18n('nui.table.page.page-number-of') + ' '}
                    <span className="-totalPages">{table.getPageCount()}</span>
                </span>
            </div>
            <div className="-next">
                <button
                    type="button"
                    disabled={!table.getCanNextPage()}
                    className="-btn"
                    onClick={() => {
                        inputRef.current.value = (table.getState().pagination.pageIndex + 2).toString();
                        table.nextPage();
                    }}
                >
                    {i18n('nui.table.next')}
                </button>
            </div>
        </div>
    );
}

function PaginationNg<T>({ table }: { table: Table<T> }) {
    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        table.setPageIndex(value - 1);
    };

    return (
        <div className="-mui-pagination">
            <div className="-mui-pagination-center">
                <MuiPagination count={table.getPageCount()} onChange={handleChange} />
            </div>
        </div>
    );
}

export type RowSelectionOpts = {
    rowSelectionState: RowSelectionState;
    setRowSelectionState: (RowSelectionState) => void;
};
